import { useMemo } from "react";
import { matchesSubtags } from "@majourneys/shared";
import type { DiscoverItem } from "../types";
import type { DiscoverState } from "../state/discover-reducer";
import { isTrip } from "../lib/is-trip";
import { matchesSearch } from "../lib/matches-search";
import { useDiscoverContext, useDiscoverItems } from "../state/discover-context";

export function filterItems(
  items: readonly DiscoverItem[],
  state: Pick<
    DiscoverState,
    | "activeTab"
    | "visibility"
    | "searchQuery"
    | "selectedCitySlug"
    | "utilityMode"
    | "aiMatchSlugs"
    | "selectedSubtags"
  >,
): DiscoverItem[] {
  // AI Chat override — when the user is on the chat tab and the latest
  // assistant turn produced matches, restrict the map to those slugs
  // only. Skips every other filter (tab/visibility/city/query) because
  // the AI answer is already curated.
  if (state.utilityMode === "chat" && state.aiMatchSlugs.length > 0) {
    const allow = new Set(state.aiMatchSlugs);
    return items.filter((item) => allow.has(item.sourceSlug));
  }

  const query = state.searchQuery.trim().toLowerCase();
  return items.filter((item) => {
    // A multi-stop trip is a route, not a place, so the browse does not carry
    // it. Dropped first, before anything else looks at the row, so the grid,
    // the map and the counts cannot disagree about what is on screen. The
    // phone applies the same rule. See lib/is-trip.ts.
    if (isTrip(item)) return false;

    if (!item.tabs.includes(state.activeTab)) return false;

    if (state.visibility === "bookable" && item.mode !== "bookable") return false;
    if (state.visibility === "explore" && item.mode !== "explore") return false;

    if (state.selectedCitySlug && item.citySlug !== state.selectedCitySlug) return false;

    if (!matchesSearch(item, query)) return false;

    if (!matchesSubtags(item, state.selectedSubtags)) return false;

    return true;
  });
}

export function useFilteredItems(): DiscoverItem[] {
  const { state } = useDiscoverContext();
  const items = useDiscoverItems();
  const {
    activeTab,
    visibility,
    searchQuery,
    selectedCitySlug,
    utilityMode,
    aiMatchSlugs,
    selectedSubtags,
  } = state;
  return useMemo(
    () =>
      filterItems(items, {
        activeTab,
        visibility,
        searchQuery,
        selectedCitySlug,
        utilityMode,
        aiMatchSlugs,
        selectedSubtags,
      }),
    [
      items,
      activeTab,
      visibility,
      searchQuery,
      selectedCitySlug,
      utilityMode,
      aiMatchSlugs,
      selectedSubtags,
    ],
  );
}
